import type { InterventionResponse } from "../services/HomeService";
import type { GlucoseCurveChartPoint } from "../components/HomePage/GlucoseCurveComparisonPanel";

type CurvePoint = { time_min: number; glucose_mg_dl: number };

export const buildInterventionChartData = (
  apiData: InterventionResponse | null | undefined,
  curvePoints: CurvePoint[],
  dropMgDl: number
): GlucoseCurveChartPoint[] => {
  const toChartPoint = (p: CurvePoint) => ({
    t: p.time_min,
    value: Math.round(p.glucose_mg_dl),
  });

  const noActionPts =
    apiData?.glucose_forecast?.no_action_curve?.points?.map(toChartPoint) ??
    curvePoints.map(toChartPoint);

  const actionPts =
    apiData?.glucose_forecast?.intervention_curve?.points?.map(toChartPoint) ??
    noActionPts.map((p) => ({
      t: p.t,
      value: Math.max(p.value - dropMgDl, p.value * 0.85),
    }));

  const allT = new Set([
    ...noActionPts.map((p) => p.t),
    ...actionPts.map((p) => p.t),
  ]);

  const noMap = Object.fromEntries(noActionPts.map((p) => [p.t, p.value]));
  const acMap = Object.fromEntries(actionPts.map((p) => [p.t, p.value]));

  return [...allT].sort((a, b) => a - b).map((t) => ({
    t,
    noAction: noMap[t] ?? null,
    withAction: acMap[t] ?? null,
  }));
};

export const getActionWindowMinutesRemaining = (
  apiData: InterventionResponse | null | undefined,
  fallbackPeakMin = 30
): number => {
  if (apiData?.best_window_minutes_remaining != null) {
    return Math.max(0, apiData.best_window_minutes_remaining);
  }
  return Math.max(0, Math.round(fallbackPeakMin * 0.6));
};
